/*
 * Copyright (c) 2021 WIT Software. All rights reserved.
 *
 * WIT Software Confidential and Proprietary information. It is strictly forbidden for 3rd parties to modify, decompile,
 * disassemble, defeat, disable or circumvent any protection mechanism; to sell, license, lease, rent, redistribute or
 * make accessible to any third party, whether for profit or without charge.
 *
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';

import {
  AssetOption,
  BadgeType,
  EntryPoint,
  Icons,
  OptionUtils,
  ParentalRatingOptionsType,
  Program,
  TimeUtils,
  WatchOption,
} from '@wit/vtv-x-foundation-lib';
import { ActionButton } from 'types/components/ActionButtonsGroup';
import fallbackThumbnail from 'assets/images/fallbackicon_large.svg';
import { useResizeObserver } from 'hooks/useResizeObserver';
import translate from 'services/i18n/translate';
import ParentalControl from 'core/services/core/ParentalControl';
import { Selectors as ChannelsSelectors } from 'core/store/ducks/epg/channels';
import { useDispatch, useSelector } from 'react-redux';
import { StoreState } from 'store/store';
import * as Options from 'store/ducks/programInfo/bar/options';
import * as RunOptions from 'store/ducks/programInfo/bar/runOptions';
import { useRecordingOptions } from 'hooks/useRecordingsOptions';
import { ButtonOption } from 'types/components/SelectOptions';
import Metadata from './Metadata';
import { ActionButtonsGroup, BadgesGroup, FloatingPagePortal, ImageLoader, Modal } from '..';
import { useRouter } from '../../../hooks/useRouter';
import { Selectors as PlayerSelectors } from '../../../store/ducks/player';

const ANIMATION_DURATION = 600;
const MOBILE_SCREEN_WIDTH_BREAK_POINT = 1080;

interface Props {
  program?: Program;
  onClose?: () => void;
  menuItemId?: number;
}

interface ConfirmationModalProps {
  message: string;
  option: AssetOption;
  callBack: (option: AssetOption) => void;
}

interface OptionsModalProps {
  title: string;
  subtitle: string;
  options: ButtonOption[] | undefined;
  buttons: ButtonOption[];
}

function ProgramInfoBar({ program, onClose, menuItemId }: Props): JSX.Element | null {
  const router = useRouter();
  const channel = useSelector((state: StoreState) =>
    ChannelsSelectors.getChannelByEpgId(state, program?.channelEpgId || 0)
  );

  const [buttons, setButtons] = useState<ActionButton[]>([]);

  const [isClosing, setIsClosing] = useState(false);
  const [inMobileScreen, setInMobileScreen] = useState(false);
  const [showOptionsModal, setShowOptionsModal] = useState(false);
  const [optionsModalProps, setOptionsModalProps] = useState<OptionsModalProps>();

  const mainDivRef = useRef<HTMLDivElement>(null);
  const mainDivDimensions = useResizeObserver(mainDivRef);
  const currentPlayingAsset = useSelector(PlayerSelectors.currentAsset);
  const isProcessing = useSelector(Options.Selectors.isProcessing);
  const isProcessed = useSelector(Options.Selectors.isProcessed);
  const options = useSelector(Options.Selectors.getOptions);
  const dispatch = useDispatch();

  useEffect(() => {
    if (program) {
      dispatch(Options.Creators.request(program));
    }
  }, [dispatch, program]);

  useEffect(() => {
    if (!mainDivDimensions.width) {
      return;
    }

    if (mainDivDimensions.width > MOBILE_SCREEN_WIDTH_BREAK_POINT) {
      if (inMobileScreen) {
        setInMobileScreen(false);
      }

      return;
    }

    if (!inMobileScreen) {
      setInMobileScreen(true);
    }
  }, [inMobileScreen, mainDivDimensions.width]);

  useEffect(() => {
    if (!isClosing || !onClose) {
      return undefined;
    }

    const timeoutId = setTimeout(onClose, ANIMATION_DURATION);

    return () => {
      clearTimeout(timeoutId);
    };
  }, [isClosing, onClose]);

  const [confirmationModal, setConfirmationModal] = useState<ConfirmationModalProps | undefined>();
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const runOptionIsProcessed = useSelector((state: StoreState) => RunOptions.Selectors.isProcessed(state));

  const dispatchRecordingOption = useCallback(
    (option: AssetOption) => {
      dispatch(RunOptions.Creators.runOption(option));
    },
    [dispatch]
  );

  const recordingOptions = useRecordingOptions(
    setConfirmationModal,
    setShowConfirmationModal,
    runOptionIsProcessed,
    dispatchRecordingOption
  );

  const getChildOptions = <T extends AssetOption>(option: T, callback: (option: T) => void) => {
    const watchOptionsButtons = option.children?.map((childOption) => {
      const modalButton: ButtonOption = {
        id: childOption.key,
        text: childOption.title,
        icon: {
          uri: childOption.channel?.logo?.color?.uri || '',
          alt: childOption.channel?.title || '',
        },
        onClick: () => callback(childOption as T), // assuming that children have the same type as parent
      };

      return modalButton;
    });
    return watchOptionsButtons;
  };

  const watchOptionsCallback = useCallback(
    (option: WatchOption) => {
      setShowOptionsModal(false);
      if (program && channel) {
        const isLiveNow = TimeUtils.isBetweenInclusive(program.startDate, program.endDate, TimeUtils.now());
        const restart = option.progress === 0;
        router.push({
          asset: isLiveNow ? channel : program,
          state: { forceAutoPlay: true, restartIfAvailable: restart },
          entryPoint: EntryPoint.TV_GUIDE,
          menuItemId,
        });
      }
    },
    [channel, menuItemId, program, router]
  );

  const watchOptions = useCallback(
    (option: WatchOption) => {
      if (option.children?.length) {
        const childOptions = getChildOptions(option, watchOptionsCallback);
        const title = translate['buttons.watchOptions'];
        const subtitle = translate.modalSubTitleWatchOptions;
        const modalButtons = [
          {
            id: 'modalButtonCancel',
            text: translate['modal.button.cancel'],
            onClick: () => {
              setShowOptionsModal(false);
            },
          },
        ];

        setOptionsModalProps({
          options: childOptions,
          title,
          subtitle,
          buttons: modalButtons,
        });
        setShowOptionsModal(true);
      } else {
        watchOptionsCallback(option);
      }
    },
    [watchOptionsCallback]
  );

  useEffect(() => {
    if (program && channel) {
      const isLiveNow = TimeUtils.isBetweenInclusive(program.startDate, program.endDate, TimeUtils.now());
      const isTheSameAsset = currentPlayingAsset?.id === program.id;

      const assetButtons: ActionButton[] = options.map((option) => ({
        ...option,
        text: option.title,
        onClick: () => {
          if (OptionUtils.isRecording(option)) {
            recordingOptions.runRecordingOption(option);
            return;
          }
          if (OptionUtils.isWatch(option)) {
            watchOptions(option);
          }
        },
      }));

      let infoButton: ActionButton = {
        key: 'programInfo',
        text: translate['buttons.info'],
      };
      if (isLiveNow && program.channelEpgId && channel) {
        if (!isTheSameAsset) {
          infoButton = {
            ...infoButton,
            onClick: () => {
              router.push({
                asset: channel,
                state: { forceAutoPlay: true },
                entryPoint: EntryPoint.TV_GUIDE,
                menuItemId,
              });
            },
          };
        }
      } else {
        infoButton = {
          ...infoButton,
          onClick: () => {
            router.push({ asset: program, entryPoint: EntryPoint.TV_GUIDE, menuItemId });
          },
        };
      }
      setButtons([...assetButtons, infoButton]);
    }
  }, [router, channel, program, currentPlayingAsset?.id, menuItemId, options, recordingOptions, watchOptions]);

  if (!program) {
    return null;
  }

  const isAdultContent = ParentalControl.UI.isAdult(program);
  const isLocked = ParentalControl.UI.getAssetParentalState(program) !== ParentalRatingOptionsType.SHOW;

  return (
    <>
      <Modal
        title={optionsModalProps?.title}
        subtitle={optionsModalProps?.subtitle}
        setIsShowing={setShowOptionsModal}
        isShowing={showOptionsModal}
        bodyButtons={optionsModalProps?.options}
        buttons={optionsModalProps?.buttons}
        customClassName="watchtv-options"
      />
      <Modal
        title={translate['modal.title.confirmation']}
        message={confirmationModal?.message}
        setIsShowing={setShowConfirmationModal}
        isShowing={showConfirmationModal}
        buttons={[
          {
            id: 'modalButtonConfirm',
            text: translate['modal.button.confirm'],
            onClick: () => confirmationModal?.callBack(confirmationModal.option),
          },
          {
            id: 'modalButtonCancel',
            text: translate['modal.button.cancel'],
            onClick: () => setShowConfirmationModal(false),
          },
        ]}
      />
      <FloatingPagePortal
        ref={mainDivRef}
        className={`pi-bar ${isClosing ? 'closing' : ''}`}
        modalMode={inMobileScreen}
        unlockBackgroundPageScroll={!inMobileScreen}
      >
        <div className="pi-bar-container" id="ProgramInfoBar_container">
          <div className="pi-bar-image">
            <div className="pi-bar-image-container" key={program.id}>
              <ImageLoader
                src={!isAdultContent && program?.images?.card ? program?.images?.card : fallbackThumbnail}
                bemModifier="programInfoBarPoster"
              />
            </div>
          </div>
          <div className="pi-bar-button-container" id="ProgramInfoBar_buttons">
            <div id="ProgramInfoBar_button___close" className="close-button" onClick={() => setIsClosing(true)} />
          </div>
          <div className="pi-bar-details">
            <h2 id="ProgramInfoBar_title" className="no-margin text-elipsis one-line">
              {program.title}
            </h2>
            <Metadata program={program} />

            {program.synopsis && !isAdultContent ? (
              <p
                id="ProgramInfoBar_synopsis"
                className={`pi-bar-synopsis text-elipsis ${inMobileScreen ? 'five-lines' : 'two-lines'}`}
              >
                {program.synopsis}
              </p>
            ) : null}

            {isAdultContent ? (
              <p id="ProgramInfoBar_adultContent" className="margin-top-10-imp">
                {translate['programInfoBar.adultContent.description']}
              </p>
            ) : null}

            {isLocked ? (
              <BadgesGroup badges={[{ type: BadgeType.LOCK, icon: Icons.LOCK }]} bemBlock="ProgramInfoBarBadges" />
            ) : null}

            {!isAdultContent && buttons.length && isProcessed && !isProcessing ? (
              <ActionButtonsGroup
                className={isLocked ? 'no-margin' : ''}
                data={buttons}
                small={inMobileScreen}
                bemBlock="ProgramInfoBarButtons"
              />
            ) : null}
          </div>
        </div>
      </FloatingPagePortal>
    </>
  );
}

export default React.memo(ProgramInfoBar);
